<template>
  <InputWrapper
    :label="label ?? undefined"
    :name="name ?? undefined"
    :disabled="disabled"
    :mini-label="!!value"
    :error-messages="allErrorMessages"
    :hint="hint ?? undefined"
  >
    <flat-pickr
      :value="value || null"
      :config="config"
      :disabled="disabled"
      :placeholder="label ?? undefined"
      data-input
      @on-change="handleChange"
      @on-close="handleClose"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
    />

    <template #append>
      <v-icon data-toggle>calendar_today</v-icon>
    </template>
  </InputWrapper>
</template>

<script setup lang="ts">
import { endOfDay, isAfter, isBefore, isEqual, isValid } from 'date-fns'
import { computed, nextTick, toRef, watch } from 'vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.min.css'
import InputWrapper from '@/components/vision/Inputs/InputWrapper.vue'
import { useFormField } from '@/components/vision/next/forms/useFormAdapter'

/**
 * Usage in a Composition API component:
 *
 * import { ref } from 'vue'
 *
 * const arrivalTime = ref<Date | null>(new Date())
 * const validateArrivalTime = (value: Date | null): string | undefined =>
 *   value && value > new Date() ? 'Arrival cannot be in the future' : undefined
 *
 * <VsDatePickerNext
 *   :value="arrivalTime"
 *   label="Arrived at"
 *   :rules="[validateArrivalTime]"
 *   required
 *   @input="arrivalTime = $event"
 * />
 *
 * `value` and `input` use `Date | null`. Each rule receives that same type and returns
 * an error message or `undefined`.
 */
type DatePickerRule = (value: Date | null) => string | undefined

const props = withDefaults(
  defineProps<{
    value?: Date | null
    label?: string | null
    name?: string | null
    hint?: string | null
    disabled?: boolean
    required?: boolean
    canSelectPast?: boolean
    canSelectFuture?: boolean
    enableTime?: boolean
    rules?: DatePickerRule[]
  }>(),
  {
    value: null,
    label: null,
    name: null,
    hint: null,
    disabled: false,
    required: false,
    canSelectPast: false,
    canSelectFuture: true,
    enableTime: true,
    rules: () => [],
  },
)

const emit = defineEmits<{
  (event: 'input', value: Date | null): void
  (event: 'focus', value: FocusEvent): void
  (event: 'blur', value: FocusEvent): void
}>()

const valueRef = toRef(props, 'value')
const localRules: (() => string | undefined)[] = props.rules.map(
  (rule) => () => rule(valueRef.value),
)

localRules.push(() => {
  if (!props.canSelectPast && valueRef.value && isBefore(valueRef.value, new Date())) {
    return `${props.label ?? 'Date'} cannot be in the past`
  }

  return undefined
})

localRules.push(() => {
  // Date-only pickers allow any time today; otherwise "future" means after now.
  const latest = props.enableTime ? new Date() : endOfDay(new Date())
  if (!props.canSelectFuture && valueRef.value && isAfter(valueRef.value, latest)) {
    return `${props.label ?? 'Date'} cannot be in the future`
  }

  return undefined
})

const { allErrorMessages, hasErrorMessages, runValidation } = useFormField({
  value: valueRef,
  required: props.required,
  localRules,
})

const config = computed(() => ({
  defaultDate: null,
  altInput: true,
  altInputClass: 'input',
  altFormat: props.enableTime ? 'M d, Y H:i' : 'M d, Y',
  dateFormat: props.enableTime ? 'Y-m-dTH:i:S' : 'Y-m-d',
  enableTime: props.enableTime,
  time_24hr: true,
  allowInput: false,
  minuteIncrement: 15,
  wrap: true,
  minDate: props.canSelectPast ? null : 'today',
  maxDate: props.canSelectFuture ? null : 'today',
}))

function handleChange(selectedDates: Date[]) {
  const selectedDate = selectedDates[0] ?? null
  const nextValue = selectedDate && isValid(selectedDate) ? new Date(selectedDate) : null

  if (
    (nextValue === null && valueRef.value === null) ||
    (nextValue !== null && valueRef.value !== null && isEqual(nextValue, valueRef.value))
  ) {
    return
  }

  emit('input', nextValue)
  nextTick(() => runValidation())
}

function handleClose() {
  nextTick(() => runValidation())
}

watch(valueRef, () => {
  if (hasErrorMessages.value) {
    runValidation()
  }
})
</script>
